import { c as _c } from "react/compiler-runtime";
import { getErrorMessage, postHostedOnboardingSkipPasskeyRegisterMutation } from '@onefootprint/fetch-hosted';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoPasskey40, IcoWarning40 } from '@onefootprint/icons';
import type { ActionKind, RegisterPasskeyAttemptContext } from '@onefootprint/request-types';
import { BottomSheet, Button, LinkButton, PageShell } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import useBiometricInit, { unwrapPasskeyError } from './use-register-biometric';
import getSkipReason from './utils/get-skip-reason';
type RegisterPasskeyProps = {
  deviceInfo: DeviceInfo;
  isInIframe: boolean;
  token: string;
  actionKind: ActionKind;
  onComplete: () => void;
};
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'liveness-register-passkey'
});

// Must match idv's FPCustomEvents.receivedDeviceResponseJson: idv's router listens for it and
// forwards the response to the webview redirect's sdk_args_token.
const DEVICE_RESPONSE_EVENT = 'receivedDeviceResponseJson';
const RegisterPasskey = t0 => {
  const $ = _c(91);
  const {
    deviceInfo,
    isInIframe,
    token,
    actionKind,
    onComplete
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "liveness.register"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = postHostedOnboardingSkipPasskeyRegisterMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const skipLivenessMutation = useMutation(t2);
  const biometricInitMutation = useBiometricInit();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [passkeyRegisterAttempts, setPasskeyRegisterAttempts] = useState(t3);
  const showRequestErrorToast = useRequestErrorToast();
  let t4;
  if ($[3] !== actionKind || $[4] !== biometricInitMutation || $[5] !== onComplete || $[6] !== token) {
    t4 = async () => {
      ;
      try {
        const {
          deviceResponseJson
        } = await biometricInitMutation.mutateAsync({
          token,
          actionKind
        });
        document.dispatchEvent(new CustomEvent(DEVICE_RESPONSE_EVENT, {
          detail: {
            deviceResponseJson
          }
        }));
        setTimeout(() => {
          logInfo("Passkeys registration succeeded");
          onComplete();
        }, 2500);
      } catch (t5) {
        const error = t5;
        const {
          cause,
          elapsedTimeInOsPromptMs
        } = unwrapPasskeyError(error);
        const errorMessage = getErrorMessage(cause);
        logWarn(`Failed to register passkeys for user: ${errorMessage}`, cause);
        setPasskeyRegisterAttempts(prev => [...prev, {
          errorMessage,
          elapsedTimeInOsPromptMs
        }]);
      }
    };
    $[3] = actionKind;
    $[4] = biometricInitMutation;
    $[5] = onComplete;
    $[6] = token;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRegister = t4;
  let t5;
  if ($[8] !== deviceInfo || $[9] !== isInIframe || $[10] !== onComplete || $[11] !== passkeyRegisterAttempts || $[12] !== showRequestErrorToast || $[13] !== skipLivenessMutation || $[14] !== token) {
    t5 = async () => {
      logInfo("Skipping liveness after retrying registering passkeys");
      ;
      try {
        await skipLivenessMutation.mutateAsync({
          headers: {
            "X-Fp-Authorization": token
          },
          body: {
            context: {
              reason: getSkipReason({
                deviceInfo,
                isInIframe,
                isUnavailable: false
              }),
              clientType: "web",
              numAttempts: passkeyRegisterAttempts.length,
              attempts: passkeyRegisterAttempts
            }
          }
        });
        onComplete();
      } catch (t6) {
        const error_0 = t6;
        showRequestErrorToast(error_0);
        logWarn("Failed to skip liveness after retrying registering passkeys", error_0);
      }
    };
    $[8] = deviceInfo;
    $[9] = isInIframe;
    $[10] = onComplete;
    $[11] = passkeyRegisterAttempts;
    $[12] = showRequestErrorToast;
    $[13] = skipLivenessMutation;
    $[14] = token;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleSkip = t5;
  let t6;
  bb0: {
    if (biometricInitMutation.isSuccess) {
      let t7;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <IcoPasskey40 color="primary" />;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      let t8;
      if ($[17] !== t) {
        t8 = t(_temp);
        $[17] = t;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      let t9;
      if ($[19] !== t) {
        t9 = t(_temp2);
        $[19] = t;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      let t10;
      if ($[21] !== t8 || $[22] !== t9) {
        t10 = {
          icon: t7,
          headerTitle: t8,
          headerSubtitle: t9
        };
        $[21] = t8;
        $[22] = t9;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      t6 = t10;
      break bb0;
    }
    if (passkeyRegisterAttempts.length) {
      let t7;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <IcoWarning40 color="error" />;
        $[24] = t7;
      } else {
        t7 = $[24];
      }
      let t8;
      if ($[25] !== t) {
        t8 = t(_temp3);
        $[25] = t;
        $[26] = t8;
      } else {
        t8 = $[26];
      }
      let t9;
      if ($[27] !== t) {
        t9 = t(_temp4);
        $[27] = t;
        $[28] = t9;
      } else {
        t9 = $[28];
      }
      let t10;
      if ($[29] !== t) {
        t10 = t(_temp5);
        $[29] = t;
        $[30] = t10;
      } else {
        t10 = $[30];
      }
      let t11;
      if ($[31] !== t) {
        t11 = t(_temp6);
        $[31] = t;
        $[32] = t11;
      } else {
        t11 = $[32];
      }
      let t12;
      if ($[33] !== t10 || $[34] !== t11 || $[35] !== t8 || $[36] !== t9) {
        t12 = {
          icon: t7,
          headerTitle: t8,
          headerSubtitle: t9,
          primaryButtonText: t10,
          secondaryButtonText: t11
        };
        $[33] = t10;
        $[34] = t11;
        $[35] = t8;
        $[36] = t9;
        $[37] = t12;
      } else {
        t12 = $[37];
      }
      t6 = t12;
      break bb0;
    }
    let t7;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <IcoPasskey40 color="primary" />;
      $[38] = t7;
    } else {
      t7 = $[38];
    }
    let t8;
    if ($[39] !== actionKind || $[40] !== t) {
      t8 = actionKind === "replace" ? t(_temp7) : t(_temp8);
      $[39] = actionKind;
      $[40] = t;
      $[41] = t8;
    } else {
      t8 = $[41];
    }
    let t9;
    if ($[42] !== t) {
      t9 = t(_temp9);
      $[42] = t;
      $[43] = t9;
    } else {
      t9 = $[43];
    }
    let t10;
    if ($[44] !== t) {
      t10 = t(_temp10);
      $[44] = t;
      $[45] = t10;
    } else {
      t10 = $[45];
    }
    let t11;
    if ($[46] !== t10 || $[47] !== t8 || $[48] !== t9) {
      t11 = {
        icon: t7,
        headerTitle: t8,
        headerSubtitle: t9,
        primaryButtonText: t10
      };
      $[46] = t10;
      $[47] = t8;
      $[48] = t9;
      $[49] = t11;
    } else {
      t11 = $[49];
    }
    t6 = t11;
  }
  const {
    icon,
    headerTitle,
    headerSubtitle,
    primaryButtonText,
    secondaryButtonText
  } = t6;
  let t7;
  if ($[50] !== biometricInitMutation.isPending || $[51] !== handleRegister || $[52] !== primaryButtonText || $[53] !== skipLivenessMutation) {
    t7 = primaryButtonText && <Button loading={biometricInitMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={handleRegister} fullWidth={true} size="large">{primaryButtonText}</Button>;
    $[50] = biometricInitMutation.isPending;
    $[51] = handleRegister;
    $[52] = primaryButtonText;
    $[53] = skipLivenessMutation;
    $[54] = t7;
  } else {
    t7 = $[54];
  }
  let t8;
  if ($[55] !== biometricInitMutation.isPending || $[56] !== handleSkip || $[57] !== secondaryButtonText || $[58] !== skipLivenessMutation) {
    t8 = secondaryButtonText && <Button loading={skipLivenessMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={handleSkip} fullWidth={true} size="large" variant="secondary">{secondaryButtonText}</Button>;
    $[55] = biometricInitMutation.isPending;
    $[56] = handleSkip;
    $[57] = secondaryButtonText;
    $[58] = skipLivenessMutation;
    $[59] = t8;
  } else {
    t8 = $[59];
  }
  let t9;
  if ($[60] !== biometricInitMutation.isSuccess || $[61] !== t) {
    t9 = !biometricInitMutation.isSuccess && <div className="flex justify-center items-center pt-2"><LinkButton onClick={() => setShowBottomSheet(true)}>{t(_temp11)}</LinkButton></div>;
    $[60] = biometricInitMutation.isSuccess;
    $[61] = t;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  let t10;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setShowBottomSheet(false);
    $[63] = t10;
  } else {
    t10 = $[63];
  }
  let t11;
  if ($[64] !== t) {
    t11 = t(_temp12);
    $[64] = t;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== t) {
    t12 = t(_temp13);
    $[66] = t;
    $[67] = t12;
  } else {
    t12 = $[67];
  }
  let t13;
  if ($[68] !== t12) {
    t13 = <p className="text-body-2 text-primary whitespace-pre-wrap">{t12}</p>;
    $[68] = t12;
    $[69] = t13;
  } else {
    t13 = $[69];
  }
  let t14;
  if ($[70] !== t) {
    t14 = t(_temp14);
    $[70] = t;
    $[71] = t14;
  } else {
    t14 = $[71];
  }
  let t15;
  if ($[72] !== t14) {
    t15 = <p className="text-body-2 text-primary whitespace-pre-wrap">{t14}</p>;
    $[72] = t14;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  let t16;
  if ($[74] !== t13 || $[75] !== t15) {
    t16 = <div className="flex flex-col gap-3 mb-5">{t13}{t15}</div>;
    $[74] = t13;
    $[75] = t15;
    $[76] = t16;
  } else {
    t16 = $[76];
  }
  let t17;
  if ($[77] !== showBottomSheet || $[78] !== t11 || $[79] !== t16) {
    t17 = <BottomSheet open={showBottomSheet} onClose={t10} title={t11} portalId="passkeys-register-container">{t16}</BottomSheet>;
    $[77] = showBottomSheet;
    $[78] = t11;
    $[79] = t16;
    $[80] = t17;
  } else {
    t17 = $[80];
  }
  let t18;
  if ($[81] !== t17 || $[82] !== t7 || $[83] !== t8 || $[84] !== t9) {
    t18 = <div className="flex flex-col gap-3 text-center items-center justify-center" id="passkeys-register-container">{t7}{t8}{t9}{t17}</div>;
    $[81] = t17;
    $[82] = t7;
    $[83] = t8;
    $[84] = t9;
    $[85] = t18;
  } else {
    t18 = $[85];
  }
  let t19;
  if ($[86] !== headerSubtitle || $[87] !== headerTitle || $[88] !== icon || $[89] !== t18) {
    t19 = <PageShell title={headerTitle} subtitle={headerSubtitle} headerIcon={icon}>{t18}</PageShell>;
    $[86] = headerSubtitle;
    $[87] = headerTitle;
    $[88] = icon;
    $[89] = t18;
    $[90] = t19;
  } else {
    t19 = $[90];
  }
  return t19;
};
export default RegisterPasskey;
function _temp($) {
  return $.success.title;
}
function _temp2($_0) {
  return $_0.success.subtitle;
}
function _temp3($_1) {
  return $_1.retry.title;
}
function _temp4($_2) {
  return $_2.retry.subtitle;
}
function _temp5($_3) {
  return $_3.retry.cta;
}
function _temp6($_4) {
  return $_4.retry.skip;
}
function _temp7($_5) {
  return $_5.titleReplace;
}
function _temp8($_6) {
  return $_6.titleAdd;
}
function _temp9($_7) {
  return $_7.subtitle;
}
function _temp10($_8) {
  return $_8.cta;
}
function _temp11($_9) {
  return $_9.learnMore.cta;
}
function _temp12($_10) {
  return $_10.learnMore.title;
}
function _temp13($_11) {
  return $_11.learnMore.content1;
}
function _temp14($_12) {
  return $_12.learnMore.content2;
}